import { Listing, ListingStatus, AnalyticsData, ApiResult } from '../../../types';
import { SearchFilters } from '../../repository';
import { api } from '../../d1';
import { ListingMapper } from '../mappers/listing.mapper';
import { filterListings } from '../../domain/listings/filters';
import { sortListings } from '../../domain/listings/sorting';
import { geocodeAddress } from '../../../utils/shipping';

export class D1ListingsRepo {
    async getListingById(id: string): Promise<Listing | undefined> {
        try {
            const row = await api.listings.get(id);
            if (!row) return undefined;
            return ListingMapper.toDomain(row);
        } catch (e) {
            console.error('D1 getListingById error', e);
            return undefined;
        }
    }

    async getListings(filters?: SearchFilters): Promise<Listing[]> {
        try {
            const params: any = {};
            if (filters) {
                if (filters.category && filters.category !== 'all') params.category = filters.category;
                if (filters.query) params.query = filters.query;
                if (filters.priceMin !== undefined) params.min_price = filters.priceMin;
                if (filters.priceMax !== undefined) params.max_price = filters.priceMax;
                if (filters.capacity !== undefined) params.min_guests = filters.capacity;
                // 'relevance' is resolved client-side by sortListings; sending
                // it would just be an unknown value the worker ignores.
                if (filters.sort && filters.sort !== 'relevance') params.sort = filters.sort;
                if (filters.limit !== undefined) params.limit = filters.limit;

                // Map additional search filters to worker params
                if (filters.transactionType) params.transaction_type = filters.transactionType;
                if (filters.parking) params.parking = 'true';
                if (filters.ownCateringAllowed) params.own_catering = 'true';
                if (filters.corkageAllowed) params.corkage = 'true';
                if (filters.minRating !== undefined) params.min_rating = filters.minRating;
                if (filters.minArea !== undefined) params.min_area = filters.minArea;
                
                if (filters.seatingTheater !== undefined) params.theater = filters.seatingTheater;
                if (filters.seatingSchool !== undefined) params.school = filters.seatingSchool;
                if (filters.seatingBanquet !== undefined) params.banquet = filters.seatingBanquet;
                if (filters.seatingCocktail !== undefined) params.cocktail = filters.seatingCocktail;

                if (filters.amenities && filters.amenities.length > 0) {
                    params.amenities = filters.amenities.join(',');
                }
                if (filters.eventTypes && filters.eventTypes.length > 0) {
                    params.eventTypes = filters.eventTypes.join(',');
                }
            }

            // Geocode location client-side and pass coordinates/radius to worker
            let effectiveFilters = filters;
            if (filters && filters.location && filters.radius) {
                try {
                    const center = await geocodeAddress(filters.location);
                    if (center) {
                        params.lat = center.lat;
                        params.lng = center.lng;
                        params.radius = filters.radius;
                        effectiveFilters = { ...filters, center: { lat: center.lat, lng: center.lng } };
                    } else {
                        params.location = filters.location;
                    }
                } catch (e) {
                    console.error('Geocoding error during search', e);
                    params.location = filters.location;
                }
            } else if (filters && filters.location) {
                params.location = filters.location;
            }

            const rows = await api.listings.list(params);
            const allListings = rows.map(r => ListingMapper.toDomain(r));

            // Run client-side filterListings as a fallback/safeguard
            const filtered = filterListings(allListings, effectiveFilters);
            const finalResult = effectiveFilters ? filtered : filtered.filter(l => l.status === ListingStatus.APPROVED || !l.status);

            return sortListings(finalResult, effectiveFilters?.sort);
        } catch (e) {
            // Propagate so React Query gets a real error state — a silent []
            // is indistinguishable from "no listings exist" in the UI.
            console.error('D1 getListings error', e);
            throw e;
        }
    }

    async getListingsCount(): Promise<number | null> {
        try {
            return await api.listings.count();
        } catch (e) {
            console.error('D1 getListingsCount error', e);
            return null; // Return null on error to differentiate from 0 listings
        }
    }

    async getHomepageListings(): Promise<{ listings: Listing[]; totalCount: number | null }> {
        try {
            const res = await api.listings.listWithMeta({ limit: 4, sort: 'newest', include_count: true });
            const allListings = (res?.results || []).map((r: any) => ListingMapper.toDomain(r));
            const totalCount = res?.meta?.total ?? null;
            return { listings: allListings, totalCount };
        } catch (e) {
            console.error('D1 getHomepageListings error', e);
            throw e;
        }
    }

    async getAdminListings(): Promise<Listing[]> {
        try {
            const rows = await api.listings.list({ status: 'all' });
            const all = rows.map(r => ListingMapper.toDomain(r));
            // Sort by date newest first
            return all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        } catch { return []; }
    }

    async getMyListings(userId: string): Promise<Listing[]> {
        try {
            const rows = await api.listings.list({ seller_id: userId, status: 'all' });
            return rows
                .map(r => ListingMapper.toDomain(r))
                .filter(l => l.seller.id === userId);
        } catch { return []; }
    }

    async addListing(listing: Listing, userId: string): Promise<ApiResult<Listing>> {
        try {
            const row = ListingMapper.toRow(listing, userId);
            const sanitizedRow = ListingMapper.deSanitize(row);
            const res = await api.listings.upsert(sanitizedRow);
            if (res.success) return { success: true, data: listing };
            return { success: false, error: res.error || 'Failed to add listing' };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    }

    async updateListing(listing: Listing, userId: string): Promise<ApiResult<Listing>> {
        try {
            const row = ListingMapper.toRow(listing, userId);
            const sanitizedRow = ListingMapper.deSanitize(row);
            const res = await api.listings.upsert(sanitizedRow);
            if (res.success) return { success: true, data: listing };
            return { success: false, error: res.error || 'Failed to update listing' };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    }

    async updateListingStatus(id: string, status: ListingStatus): Promise<boolean> {
        try {
            const res = await api.listings.updateStatus(id, status);
            return !!res?.success;
        } catch { return false; }
    }

    /** Admin-only: provenance/trust columns. Null clears back to "derived". */
    async updateListingTrust(id: string, fields: Record<string, string | boolean | null>): Promise<boolean> {
        try {
            const res = await api.listings.updateTrust(id, fields);
            return !!res?.success;
        } catch { return false; }
    }

    async updateListingImages(id: string, images: string[]): Promise<boolean> {
        try {
            const res = await api.listings.updateImages(id, images);
            return !!res?.success;
        } catch { return false; }
    }

    async deleteListing(id: string): Promise<ApiResult<void>> {
        try {
            const res = await api.listings.remove(id);
            if (res.success) return { success: true, data: undefined };
            return { success: false, error: res.error || 'Failed to delete' };
        } catch (e: any) { return { success: false, error: e.message }; }
    }

    async toggleListingStatus(listingId: string, currentStatus: string): Promise<{ success: boolean; error?: string }> {
        const status = (currentStatus || '').toLowerCase().trim();
        // Check for both the localized value and common keys/internal values
        const isApproved =
            status === 'schválený' ||
            status === 'approved' ||
            status === ListingStatus.APPROVED.toLowerCase();

        const newStatus = isApproved ? ListingStatus.HIDDEN : ListingStatus.APPROVED;

        if (api.listings) {
            try {
                const res = await api.listings.updateStatus(listingId, newStatus);
                return { success: !!res?.success, error: res?.error };
            } catch (e: any) {
                console.error('[D1ListingsRepo] Toggle status error:', e);
                return { success: false, error: e.message };
            }
        }
        return { success: false, error: 'API not configured' };
    }

    async updateListingProfileRequirement(sellerId: string, required: boolean): Promise<{ success: boolean }> {
        try {
            const res = await api.listings.updateProfileRequirement(sellerId, required);
            return { success: !!res?.success };
        } catch {
            return { success: false };
        }
    }

    async getListingAnalytics(listingId: string, days?: number): Promise<AnalyticsData[]> {
        try {
            if (api.listings) {
                const results = await api.listings.getAnalytics(listingId);
                if (results) {
                    return results.map((r: any) => ({
                        date: r.date,
                        views: r.views || 0,
                        clicks: r.clicks || 0
                    }));
                }
            }
        } catch (e) {
            console.error('getListingAnalytics error', e);
        }

        const count = days || 7;
        return Array.from({ length: count }).map((_, i) => ({
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
            views: 0,
            clicks: 0
        })).reverse();
    }

    async getListingsAnalyticsBulk(ids: string[], days?: number): Promise<AnalyticsData[]> {
        try {
            if (api.listings?.getAnalyticsBulk) {
                const results = await api.listings.getAnalyticsBulk(ids);
                if (results) {
                    return results.map((r: any) => ({
                        date: r.date,
                        views: r.views || 0,
                        clicks: r.clicks || 0,
                        listingId: r.listing_id
                    }));
                }
            }
        } catch (e) {
            console.error('getListingsAnalyticsBulk error', e);
        }

        const count = days || 7;
        return Array.from({ length: count }).map((_, i) => ({
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
            views: 0,
            clicks: 0
        })).reverse();
    }

    async trackListingClick(listingId: string): Promise<void> {
        try {
            if (api.listings) {
                await api.listings.trackEvent(listingId);
            }
        } catch (e) {
            console.error('trackListingClick error', e);
        }
    }
    async getFavoriteListingsByIds(ids: string[]): Promise<Listing[]> {
        try {
            const rows = await api.favorites.listDetails(ids);
            return rows.map(r => ListingMapper.toDomain(r));
        } catch { return []; }
    }
}
